import { Modal, Form, Input, Button, Avatar } from "antd";
import { useState } from "react";
import { ColorPickerWithLabel } from "@components/Settings/OverviewSettings/ColorPickerWithLabel";
import { useDispatch } from "react-redux";
import { createCalendarTag, updateCalendarTag } from "@store/actions/team.actions";
import { FaBed, FaExclamationTriangle, FaCircle } from "react-icons/fa";
import { FaDumbbell, FaPersonWalkingLuggage, FaTrophy } from "react-icons/fa6";
import { IoMdAirplane } from "react-icons/io";
import { IoMan } from "react-icons/io5";
import { GiLeg } from "react-icons/gi";
import { BsCameraReelsFill } from "react-icons/bs";
import { MdOutlineTimer, MdRestaurant } from "react-icons/md";
import React from "react";


const DEFAULT_COLOR = "#1777FF";

const ICON_OPTIONS: { key: string; icon: React.ReactNode }[] = [
  { key: "bed", icon: <FaBed /> },
  { key: "plane", icon: <IoMdAirplane /> },
  { key: "luggage", icon: <FaPersonWalkingLuggage /> },
  { key: "bicep", icon: <FaDumbbell /> },
  { key: "leg", icon: <GiLeg /> },
  { key: "fork", icon: <MdRestaurant /> },
  { key: "film", icon: <BsCameraReelsFill /> },
  { key: "timer", icon: <MdOutlineTimer /> },
  { key: "trophy", icon: <FaTrophy /> },
  { key: "person", icon: <IoMan /> },
  { key: "warning", icon: <FaExclamationTriangle /> },
  { key: "circle", icon: <FaCircle /> },
];

const DEFAULT_ICON = ICON_OPTIONS[0].key;

interface CalendarTag {
  id: string;
  label: string;
  color: string;
  icon: string;
}

interface CreateTagDialogProps {
  open: boolean;
  editTag?: CalendarTag | null;
  onClose: () => void;
}

export const CreateTagDialog = ({ open, editTag, onClose }: CreateTagDialogProps) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const isEdit = !!editTag;

  const watchedColor: string = Form.useWatch("color", form) ?? DEFAULT_COLOR;
  const watchedIcon: string = Form.useWatch("icon", form) ?? DEFAULT_ICON;
  const previewIconNode = ICON_OPTIONS.find((o) => o.key === watchedIcon)?.icon;

  const handleOpen = () => {
    if (editTag) {
      form.setFieldsValue({ label: editTag.label, color: editTag.color, icon: editTag.icon });
    } else {
      form.resetFields();
      form.setFieldsValue({ color: DEFAULT_COLOR, icon: DEFAULT_ICON });
    }
  };

  const handleSubmit = async (values: { label: string; color: string; icon: string }) => {
    setSubmitting(true);
    try {
      if (isEdit && editTag) {
        await (dispatch as any)(updateCalendarTag(editTag.id, values));
      } else {
        await (dispatch as any)(createCalendarTag(values));
      }
      onClose();
    } catch {
      // error shown via audioMessage in the action
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={isEdit ? "Edit Tag" : "Create Tag"}
      open={open}
      onCancel={onClose}
      afterOpenChange={(visible) => { if (visible) handleOpen(); }}
      footer={null}
      destroyOnHide
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
          <Form.Item
            name="label"
            label="Label"
            style={{ flex: 1, marginBottom: 24 }}
            rules={[{ required: true, message: "Label is required" }, { max: 100 }]}
          >
            <Input placeholder="e.g. Film Session" />
          </Form.Item>
          <Form.Item name="color" style={{ marginBottom: 24 }} rules={[{ required: true }]}>
            <ColorPickerFormBridge />
          </Form.Item>
        </div>

        <Form.Item label="Icon">
          <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
            <Form.Item name="icon" noStyle rules={[{ required: true, message: "Pick an icon" }]}>
              <IconPicker />
            </Form.Item>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1, justifyContent: "center" }}>
              <span style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 4 }}>Preview</span>
              <Avatar
                size={48}
                icon={previewIconNode}
                style={{ backgroundColor: watchedColor, flexShrink: 0, fontSize: 22 }}
              />
            </div>
          </div>
        </Form.Item>

        <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
          <Button onClick={onClose} style={{ marginRight: 8 }}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={submitting}>
            {isEdit ? "Save" : "Create"}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

const ColorPickerFormBridge = ({ value, onChange }: { value?: string; onChange?: (v: string) => void }) => (
  <ColorPickerWithLabel
    pickerId="tag-color"
    defaultColor={value ?? DEFAULT_COLOR}
    handleColorChangeParent={(_id: string, hex: string) => onChange?.(hex)}
    usePopover={true}
  />
);

const IconPicker = ({ value, onChange }: { value?: string; onChange?: (v: string) => void }) => (
  <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 36px)", gap: 8 }}>
    {ICON_OPTIONS.map(({ key, icon }) => (
      <button
        key={key}
        type="button"
        onClick={() => onChange?.(key)}
        style={{
          width: 36,
          height: 36,
          borderRadius: 6,
          border: value === key ? "2px solid #1890ff" : "1px solid #d9d9d9",
          background: value === key ? "#e6f4ff" : "#fff",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 16,
        }}
        title={key}
      >
        {icon}
      </button>
    ))}
  </div>
);
